import { Icon, type IconName } from './Icon';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  ariaLabel?: string;
  /** Optional secondary line, e.g. the resolved theme under "System". */
  hint?: string;
  icon?: IconName;
}

interface Props<T extends string> {
  /** Accessible group name — always a translated string. */
  legend: string;
  name: string;
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  compact?: boolean;
}

/**
 * Radio group styled as segments. Wraps instead of scrolling sideways, and
 * every segment clears the 44px touch target.
 */
export function SegmentedControl<T extends string>({
  legend,
  name,
  value,
  options,
  onChange,
  compact = false,
}: Props<T>) {
  return (
    <fieldset>
      <legend className="sr-only">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <label
              key={option.value}
              className={[
                'tap-target flex cursor-pointer flex-col items-center justify-center text-center',
                compact ? 'min-w-0 flex-none px-2' : 'min-w-[7rem] flex-1 px-3',
                'rounded-full border py-2 text-sm transition-colors',
                'has-[:focus-visible]:outline has-[:focus-visible]:outline-2',
                'has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent',
                selected
                  ? 'border-accent bg-accent-soft font-medium text-fg'
                  : 'border-border-strong bg-surface text-muted hover:border-accent hover:text-fg',
              ].join(' ')}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={selected}
                onChange={() => onChange(option.value)}
                aria-label={option.ariaLabel ?? option.label}
                className="sr-only rounded-full"
              />
              {option.icon && <Icon name={option.icon} className="h-4 w-4" />}
              <span>{option.label}</span>
              {option.hint && (
                <span className="mt-0.5 text-xs text-muted">{option.hint}</span>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
